import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

function ChatInput({ onSendMessage, disabled = false, incomingTranscript, onTranscriptConsumed, mrn }) {
  const [message, setMessage] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const { user } = useAuth();
  const textareaRef = useRef(null);

  // ─── CORE REAL-TIME STRUCTURAL REFERENCES ────────────────────────────────
  const socketRef = useRef(null);
  const audioContextRef = useRef(null);
  const processorRef = useRef(null);
  const mediaStreamRef = useRef(null);

  // ─── TEXTAREA AUTO-RESIZE ─────────────────────────────────────────────────
  const adjustTextareaHeight = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = '44px';
      const scrollHeight = textareaRef.current.scrollHeight;
      const maxHeight = 120;
      textareaRef.current.style.height = Math.min(scrollHeight, maxHeight) + 'px';
      textareaRef.current.style.overflowY = scrollHeight > maxHeight ? 'auto' : 'hidden';
    }
  };

  useEffect(() => {
    adjustTextareaHeight();
  }, [message]);

  // Consume text fragments streaming down from the parent hook / Supabase table
  useEffect(() => {
    if (incomingTranscript) {
      setMessage(incomingTranscript);
      onTranscriptConsumed(); // Clear parent layout parameter state natively
      textareaRef.current?.focus();
    }
  }, [incomingTranscript, onTranscriptConsumed]);

  // ─── SINGLE WORKSPACE LIFECYCLE: CONNECT ON MOUNT ─────────────────────────
  useEffect(() => {
    let reconnectAttempts = 0;
    let intentionalClose = false;
    let timeoutId = null;

    const initializePersistentSocket = async () => {
      if (intentionalClose) return;

      try {
        const sessionJwt = user?.token || "";
        const hostEnv = process.env.REACT_APP_REALTIME_HOST || 'localhost:8080';
        const cleanHost = hostEnv.replace(/^https?:\/\//, '');

        const SYSTEM_RELAY_URL = hostEnv.includes('localhost')
          ? `ws://${cleanHost}?token=${sessionJwt}&chat_id=${mrn}`
          : `wss://${cleanHost}?token=${sessionJwt}&chat_id=${mrn}`;

        console.log('[Pipeline Startup] Connecting persistent session network socket...');
        const ws = new WebSocket(SYSTEM_RELAY_URL);
        ws.binaryType = 'arraybuffer';
        socketRef.current = ws;

        ws.onopen = () => {
          reconnectAttempts = 0; // Reset counter completely on successful channel opening
          console.log('[Pipeline Connected] Long-lived channel confirmed open.');
        };

        ws.onmessage = (event) => {
          try {
            const msg = JSON.parse(event.data);
            if (msg.event === 'transcript' && msg.text) {
              setMessage(prev => prev ? prev + ' ' + msg.text : msg.text);
            }
          } catch (e) {
            console.error('[Socket Message Parse Error]', e);
          }
        };

        ws.onerror = (error) => {
          console.error('[Socket Exception]', error);
        };

        ws.onclose = (event) => {
          if (intentionalClose) return;

          // CRITICAL: Stop reconnect loop if the server explicitly rejects authentication (Code 4001)
          if (event.code === 4001) {
            console.error('[Socket] Authentication rejected (4001). Halting connection sequence.');
            return;
          }

          console.log(`[Socket Closed] Code: ${event.code}. Attempt ${reconnectAttempts + 1} reconnect...`);

          if (reconnectAttempts < 5) {
            const delay = Math.min(1000 * 2 ** reconnectAttempts, 30000);
            reconnectAttempts++; // Safely advance iteration step

            timeoutId = setTimeout(() => {
              initializePersistentSocket();
            }, delay);
          } else {
            console.error('[Socket] Max reconnect attempts reached.');
          }
        };

      } catch (err) {
        console.error('Failed to provision secure real-time network layer:', err);
      }
    };

    console.log('[Pipeline Init] ChatInput component mounted, evaluating context for socket initialization...');
    if (mrn && user) {
      initializePersistentSocket();
    }

    // Clean cleanup cycle covering active handles and components dropping out-of-view
    return () => {
      intentionalClose = true;
      if (timeoutId) clearTimeout(timeoutId);
      if (socketRef.current) {
        console.log('[Pipeline Teardown] Cleaning up persistent session line.');
        socketRef.current.close();
      }
    };
  }, [mrn, user]);

  // ─── AUDIO FLOW MANAGEMENT ────────────────────────────────────────────────
  const startAudioStreaming = async () => {
    if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
      console.error('Cannot record: Persistent pipeline socket is not connected.');
      return;
    }

    try {

      setIsRecording(true);

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          channelCount: 1,
          sampleRate: 16000,
        },
        video: false
      });
      mediaStreamRef.current = stream;

      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      const audioContext = new AudioContextClass({ sampleRate: 16000 });
      audioContextRef.current = audioContext;

      if (audioContext.state === 'suspended') {
        await audioContext.resume();
      }

      const source = audioContext.createMediaStreamSource(stream);

      await audioContext.audioWorklet.addModule('/audioWorker.js');
      const workletNode = new AudioWorkletNode(audioContext, 'audio-processor');
      processorRef.current = workletNode;

      source.connect(workletNode);

      socketRef.current.send(JSON.stringify({ event: 'start_mic' }));

      workletNode.port.onmessage = (e) => {
        if (socketRef.current?.readyState !== WebSocket.OPEN) return;
        const float32 = e.data;
        const pcm16 = new Int16Array(float32.length);
        for (let i = 0; i < float32.length; i++) {
          const s = Math.max(-1, Math.min(1, float32[i]));
          pcm16[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
        }
        socketRef.current.send(pcm16.buffer);
      };

      console.log('[Audio Engine] Pipeline active. state =', audioContext.state);

    } catch (err) {
      console.error('Microphone hardware acquisition exception raised:', err);
      stopAudioStreaming();
    }
  };

  const stopAudioStreaming = () => {
    setIsRecording(false);

    // Step A: Inform backend relay server to dynamically close out upstream AI links immediately
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify({ event: 'stop_mic' }));
    }

    // Step B: Disconnect and free system audio resources safely to avoid data leakages
    if (processorRef.current) {
      processorRef.current.disconnect();
      processorRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => { });
      audioContextRef.current = null;
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
      mediaStreamRef.current = null;
    }
  };

  const toggleRecording = () => {
    if (isRecording) {
      stopAudioStreaming();
    } else {
      startAudioStreaming();
    }
    textareaRef.current?.focus();
  };

  // ─── STANDARD CONVERSATION COMMIT HANDLERS ────────────────────────────────
  const handleSend = () => {
    if (message.trim()) {
      if (isRecording) {
        stopAudioStreaming();
      }
      onSendMessage(message);
      setMessage('');
      if (textareaRef.current) textareaRef.current.style.height = '44px';
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleInput = (e) => {
    setMessage(e.target.value);
  };

  const isInputDisabled = disabled;

  return (
    <>
      <style>{`
        .input-wrapper {
          position: relative;
          flex: 1;
          display: flex;
          align-items: center;
        }
      `}</style>

      <div className="chat-input-container">
        <div className="input-wrapper">
          <textarea
            ref={textareaRef}
            value={message}
            onChange={handleInput}
            onKeyDown={handleKeyPress}
            placeholder={disabled ? 'Processing...' : 'Aa'}
            className="chat-input"
            disabled={isInputDisabled}
            rows="1"
          />
        </div>

        <button
          onClick={handleSend}
          className="btn-send"
          disabled={isInputDisabled || !message.trim()}
        >
          Send
        </button>

        <button
          onClick={toggleRecording}
          disabled={isInputDisabled}
          className={`btn-voice ${isRecording ? 'recording' : ''}`}
        >
          {isRecording ? (
            '⏹'
          ) : (
            <img src="/images/microphone.png" alt="mic" style={{ width: '20px' }} />
          )}
        </button>
      </div>
    </>
  );
}

export default ChatInput;